<template>
  <div class="bcd-tool">
    <!-- Upload -->
    <div class="editor-pane" v-if="!result && !error && !loading">
      <div class="pane-header">
        <span class="pane-dot input-dot" /><span class="pane-label">{{ $t('tool.BarcodeDecoder.title') }}</span>
        <span class="pane-hint">{{ $t('tool.BarcodeDecoder.tip') }}</span>
      </div>
      <el-upload ref="upload" drag action="#" :auto-upload="false" :on-change="handleFile" :limit="1" accept="image/*" :file-list="fileList">
        <i class="el-icon-picture-outline" style="font-size:42px;color:#c0c4cc" />
        <div class="el-upload__text">{{ $t('tool.BarcodeDecoder.dropHint') }}<em>{{ $t('tool.QrDecoder.clickUpload') }}</em></div>
      </el-upload>
      <div style="text-align:center;margin-top:14px" v-if="file">
        <span class="bcd-file-name"><i class="el-icon-document" /> {{ file.name }}</span>
        <el-button type="primary" size="small" :loading="loading" @click="decode">{{ $t('tool.BarcodeDecoder.btnDecode') }}</el-button>
      </div>
    </div>

    <!-- Loading -->
    <div class="editor-pane bcd-state-pane" v-if="loading">
      <i class="el-icon-loading bcd-state-icon" />
      <p class="bcd-state-text">{{ $t('tool.BarcodeDecoder.decoding') }}</p>
    </div>

    <!-- Result -->
    <div class="bcd-result" v-if="result">
      <div class="bcd-result-hd">
        <span class="pane-dot output-dot" />
        <span class="pane-label">{{ $t('tool.BarcodeDecoder.found') }}</span>
      </div>
      <div class="bcd-result-body">
        <div class="bcd-field">
          <span class="bcd-field-label">{{ $t('tool.BarcodeDecoder.content') }}</span>
          <code class="bcd-field-value">{{ result.content }}</code>
        </div>
        <div class="bcd-field" v-if="result.format">
          <span class="bcd-field-label">{{ $t('tool.BarcodeDecoder.format') }}</span>
          <el-tag size="small" type="success">{{ result.format }}</el-tag>
        </div>
      </div>
      <div class="bcd-result-actions">
        <el-button size="small" icon="el-icon-document-copy" @click="doAction('copy')">{{ $t('common.action.copy') }}</el-button>
        <el-button size="small" plain @click="reset">{{ $t('tool.BarcodeDecoder.redecode') }}</el-button>
      </div>
    </div>

    <!-- Error -->
    <div class="editor-pane bcd-state-pane" v-if="error">
      <i class="el-icon-warning bcd-state-icon bcd-state-err" />
      <p class="bcd-state-text bcd-state-err-text">{{ error }}</p>
      <el-button size="small" @click="reset" style="margin-top:8px">{{ $t('tool.BarcodeDecoder.retry') }}</el-button>
    </div>
  </div>
</template>

<script>
import { decodeBarcode } from '@/api/site'

const FORMAT_MAP = {
  CODE_128: 'CODE128', CODE_39: 'CODE39', CODE_93: 'CODE93',
  EAN_13: 'EAN-13', EAN_8: 'EAN-8', UPC_A: 'UPC-A', UPC_E: 'UPC-E',
  ITF: 'ITF-14', CODABAR: 'Codabar'
}

export default {
  name: 'BarcodeDecoderTool',
  props: { config: { type: Object, default: () => ({}) } },
  data: () => ({ file: null, fileList: [], loading: false, result: null, error: '' }),
  methods: {
    handleFile(f) {
      if (f.size > 50 * 1024 * 1024) { this.$message.error('最大50M'); return }
      this.file = f.raw; this.fileList = [f]; this.result = null; this.error = ''
    },
    async decode() {
      if (!this.file) return
      this.loading = true; this.error = ''
      try {
        const formData = new FormData()
        formData.append('file', this.file)
        const res = await decodeBarcode(formData)
        if (res.code === 200) {
          this.result = { content: res.data.content, format: FORMAT_MAP[res.data.format] || res.data.format }
        } else {
          this.error = res.message || this.$t('tool.BarcodeDecoder.notFound')
        }
      } catch (e) {
        this.error = this.$t('tool.BarcodeDecoder.decodeFail') + ': ' + (e.message || '')
      }
      this.loading = false
    },
    reset() { this.file = null; this.fileList = []; this.result = null; this.error = ''; this.loading = false },
    doAction(a) {
      if (a === 'copy' && this.result) { navigator.clipboard.writeText(this.result.content); this.$message.success(this.$t('common.status.copied')) }
      if (a === 'clear') this.reset()
    }
  }
}
</script>

<style scoped>
.bcd-tool { display:flex;flex-direction:column;gap:16px; }

.editor-pane { background:#fafbfc;border-radius:12px;border:1px solid #ebeef5;padding:16px 20px; min-width:0; overflow:hidden; }
.pane-header { display:flex;align-items:center;gap:8px;margin-bottom:12px; flex-wrap:wrap; }
.pane-dot { width:8px;height:8px;border-radius:50%;flex-shrink:0; }
.input-dot { background:#4e7cff;box-shadow:0 0 6px rgba(78,124,255,.4); }
.output-dot { background:#43e97b;box-shadow:0 0 6px rgba(67,233,123,.4); }
.pane-label { font-size:13px;font-weight:700;color:#1a1f36; }
.pane-hint { font-size:11px;color:#b0b8c4;margin-left:auto; white-space:nowrap; }
.bcd-file-name { font-size:13px;color:#606266; }

.bcd-tool ::v-deep .el-upload { width:100%; }
.bcd-tool ::v-deep .el-upload-dragger { display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:170px; max-width:100%; width:100%; box-sizing:border-box; }

.bcd-state-pane { display:flex;flex-direction:column;align-items:center;padding:36px 20px;text-align:center; }
.bcd-state-icon { font-size:36px;color:#4e7cff; }
.bcd-state-text { margin:10px 0 0;font-size:14px;color:#606266; }
.bcd-state-err { color:#f56c6c; }
.bcd-state-err-text { color:#f56c6c; }

.bcd-result { background:#f0f9eb;border:1px solid #e1f3d8;border-radius:12px;padding:20px 24px; }
.bcd-result-hd { display:flex;align-items:center;gap:8px;margin-bottom:14px; }
.bcd-result-body { display:flex;flex-direction:column;gap:12px; }
.bcd-field { display:flex;align-items:center;gap:10px; background:#fff;border:1px solid #e4e7ed;border-radius:8px;padding:10px 16px; }
.bcd-field-label { font-size:12px;color:#909399;white-space:nowrap;min-width:60px; }
.bcd-field-value { font-family:'JetBrains Mono','Fira Code','Consolas',monospace;font-size:15px;color:#1a1f36;word-break:break-all;flex:1; }
.bcd-result-actions { display:flex;gap:8px;margin-top:14px; }

@media (max-width: 640px) {
  .editor-pane { padding:12px 14px; }
  .pane-hint { display:none; }
  .bcd-tool ::v-deep .el-upload-dragger { min-height:140px;padding:20px 12px; }
  .bcd-tool ::v-deep .el-upload__text { font-size:12px; }
  .bcd-result { padding:14px 16px; }
  .bcd-field { flex-direction:column;align-items:flex-start;gap:4px; }
  .bcd-field-value { font-size:13px; }
}
</style>
